/**
 * Device Management Utility
 * Provides immediate device detection and viewport optimization
 * for perfect layout across all devices including TV browsers
 */

import logger from './Logger.js';
import UAParser from 'ua-parser-js';

class DeviceManager {
  constructor() {
    this.deviceInfo = this.detectDevice();
    this.viewport = this.calculateOptimalViewport();
    this.capabilities = this.testCapabilities();
  }

  /**
   * Detect make/model from user agent using ua-parser-js with fallbacks
   */
  detectMake(userAgent) {
    // Use navigator.userAgentData (Chromium 90+) for accurate platform detection
    // when available — avoids the AppleWebKit false positive on non-Apple devices
    const platform = (navigator.userAgentData?.platform || '').toLowerCase();
    const isNonApplePlatform = ['android', 'linux', 'windows', 'chrome os', 'chromeos'].includes(platform)
      || /Android|Windows|CrOS/i.test(userAgent)
      || (/Linux/i.test(userAgent) && !/Mac|iPhone|iPad/i.test(userAgent));

    const vendors = {
      'Samsung': /Samsung/i,
      'LG': /LG/i,
      'Sony': /Sony/i,
      // Only match Apple on genuine Apple platforms (not non-Apple with AppleWebKit)
      ...(!isNonApplePlatform ? { 'Apple': /Apple|iPhone|iPad|Mac/i } : {}),
      'Google': /Google|Pixel|Nexus/i,
      'Microsoft': /Microsoft|Windows Phone/i,
      'Amazon': /Amazon|Silk|Fire|AFT/i,
      'Roku': /Roku/i,
      'Vizio': /Vizio/i,
      'TCL': /TCL/i,
      'Hisense': /Hisense/i,
      'Philips': /Philips/i,
      'Sharp': /Sharp/i,
      'Toshiba': /Toshiba/i,
      'Panasonic': /Panasonic/i,
      'Xiaomi': /Xiaomi|MIUI|Redmi/i,
      'Huawei': /Huawei/i,
      'OnePlus': /OnePlus/i,
      'OPPO': /OPPO/i,
      'Motorola': /Motorola|Moto/i,
      'Nokia': /Nokia/i,
      'HTC': /HTC/i,
      'Asus': /Asus/i,
      'Lenovo': /Lenovo/i,
    };
    for (const [vendor, regex] of Object.entries(vendors)) {
      if (regex.test(userAgent)) return vendor;
    }
    return 'Unknown';
  }

  /**
   * Detect model from user agent with fallback patterns
   */
  detectModel(userAgent) {
    const modelPatterns = [
      /(SM-[A-Z0-9]+)/i,                // Samsung model numbers
      /(iPhone[^;,)]*)/i,               // iPhone models
      /(iPad[^;,)]*)/i,                 // iPad models
      /(Pixel[^;,)]*)/i,                // Google Pixel
      /(Nexus[^;,)]*)/i,                // Google Nexus
      /Android.*;\s*([^);]+)\s*Build/i, // Android device model
      /(Fire TV[^;,)]*)/i,              // Amazon Fire TV
      /(AFTB|AFTS|AFTT|AFTM|AFTR)[A-Z0-9]*/i, // Fire TV device codes
      /(Roku[^;,)]*)/i,                 // Roku devices
      /Windows NT[^;]*;\s*([^;)]+)/i,   // Windows devices
    ];
    for (const pattern of modelPatterns) {
      const match = userAgent.match(pattern);
      if (match && match[1]) return match[1].trim();
    }
    return 'Unknown';
  }

  /**
   * Derive the BrightSign model tier (LS/HD/XD/XT/XC) from a model string.
   * Returns null for non-BrightSign model numbers.
   */
  deriveBrightSignTier(model) {
    if (!model || typeof model !== 'string') return null;
    const upper = model.toUpperCase();
    if (upper.startsWith('LS')) return 'ls';
    if (upper.startsWith('HD')) return 'hd';
    if (upper.startsWith('XD')) return 'xd';
    if (upper.startsWith('XT')) return 'xt';
    if (upper.startsWith('XC')) return 'xc';
    return null;
  }

  /**
   * Detect device type and browser immediately on page load
   */
  detectDevice() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const touchPoints = navigator.maxTouchPoints || 0;

    // Parse user agent for device info using ua-parser-js
    const parser = new UAParser(userAgent);
    const result = parser.getResult();

    // Detect make (manufacturer) with fallback
    let make = result.device.vendor || this.detectMake(userAgent);

    // Detect model with fallback
    let model = result.device.model || this.detectModel(userAgent);

    // Detect OS — PRIMARY: ua-parser-js (returns versioned names like "Windows 10", "Android 13")
    // Client Hints as fallback only when ua-parser-js fails
    let os = result.os.name || navigator.userAgentData?.platform || platform || 'Unknown';
    let osVersion = result.os.version || 'Unknown';

    // BrightSign override — ua-parser-js doesn't know about BrightSign
    // players, so we detect them via UA substring and (preferred) the
    // window.BSDeviceInfo globals that the autorun shell injects into
    // the HTML zone. This is guarded so Android / web-browser paths are
    // completely unaffected: if neither the UA nor BSDeviceInfo match,
    // `make` / `model` / `os` retain their ua-parser-js values.
    const hasBSDeviceInfo =
      typeof window !== 'undefined' && typeof window.BSDeviceInfo !== 'undefined';
    const hasBrightSignUA =
      typeof userAgent === 'string' && /BrightSign/i.test(userAgent);
    if (hasBSDeviceInfo || hasBrightSignUA) {
      make = 'BrightSign';
      if (hasBSDeviceInfo && typeof window.BSDeviceInfo.model === 'string') {
        model = window.BSDeviceInfo.model;
      } else if (!model || model === 'Unknown') {
        model = 'BrightSign';
      }
      os = 'BrightSign OS';
      if (hasBSDeviceInfo && typeof window.BSDeviceInfo.firmwareVersion === 'string') {
        osVersion = window.BSDeviceInfo.firmwareVersion;
      }
    }

    // TV browser detection
    const isFireTV = /Silk|AFT[A-Z]/i.test(userAgent);  // AFT followed by any letter covers all Fire TV models
    const isSamsungTV = /Tizen|SMART-TV/.test(userAgent);
    const isLGTV = /webOS|Web0S/.test(userAgent);
    const isRoku = /Roku/.test(userAgent);
    const isChromecast = /CrKey/.test(userAgent);
    const isAppleTV = /AppleTV/.test(userAgent);

    // General TV detection
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const isLargeScreen = screenWidth >= 1920 && screenHeight >= 1080;
    const aspectRatio = screenWidth / screenHeight;
    const isWidescreen = aspectRatio >= 1.6; // 16:9 or wider

    // Known Android TV box detection (catches boxes that don't meet the 1920x1080 threshold)
    const isAndroidBox = /rk3\d{3}|HK1|H96|X96|Gocast|oppen|BeyondTV/i.test(userAgent);

    // BrightSign detection — a dedicated DOOH signage device running an
    // embedded Chromium HTML zone. Treat it like a TV for layout / IMA
    // / autoplay purposes, but expose a separate flag so platform-specific
    // code (e.g. hardware video zone delegation) can branch on it.
    const isBrightSign = hasBSDeviceInfo || hasBrightSignUA;
    const brightSignTier = isBrightSign ? this.deriveBrightSignTier(model) : null;

    const isTV = isFireTV || isSamsungTV || isLGTV || isRoku || isChromecast || isAppleTV ||
                 isAndroidBox || isBrightSign;

    // Mobile detection — exclude known Android TV box models that report "Android" in UA
    // and may advertise touch support despite being set-top boxes
    const isAndroidTVBox = /rk3\d{3}|HK1|H96|X96|Gocast|oppen|BeyondTV|AFT/i.test(userAgent);
    const isMobile = !isAndroidTVBox && /Mobile|Android|iPhone/.test(userAgent) && touchPoints > 0;

    // Tablet detection
    const isTablet = (touchPoints > 1 && !isMobile) ||
                     /iPad|tablet/.test(userAgent) ||
                     (touchPoints > 0 && screenWidth >= 768);

    // Browser detection
    const isChrome = /Chrome/.test(userAgent) && !/Edge|Edg/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);
    const isEdge = /Edge|Edg/.test(userAgent);

    return {
      // Device identification (OpenRTB compatible)
      make,
      model,
      os,
      osVersion,

      // Device types
      isTV,
      isMobile,
      isTablet,
      isDesktop: !isTV && !isMobile && !isTablet,

      // Specific TV brands
      isFireTV,
      isSamsungTV,
      isLGTV,
      isRoku,
      isChromecast,
      isAppleTV,
      // BrightSign DOOH signage (may be true alongside isTV=true)
      isBrightSign,
      brightSignTier,

      // Browser info
      isChrome,
      isSafari,
      isFirefox,
      isEdge,
      browserName: result.browser.name || 'Unknown',
      browserVersion: result.browser.version || 'Unknown',

      // Screen info
      screenWidth,
      screenHeight,
      aspectRatio,
      isLargeScreen,
      isWidescreen,

      // System info
      userAgent,
      platform,
      touchPoints,
      pixelRatio: window.devicePixelRatio || 1
    };
  }

  /**
   * Calculate optimal viewport dimensions for perfect layout
   */
  calculateOptimalViewport() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // For TV browsers, account for overscan and browser chrome
    if (this.deviceInfo.isTV) {
      // TVs typically have 5-10% overscan, and TV browsers may have additional chrome
      const overscanFactor = 0.95; // Use 95% of available space
      const tvSafeWidth = windowWidth * overscanFactor;
      const tvSafeHeight = windowHeight * overscanFactor;
      const offsetX = (windowWidth - tvSafeWidth) / 2;
      const offsetY = (windowHeight - tvSafeHeight) / 2;

      return {
        width: tvSafeWidth,
        height: tvSafeHeight,
        offsetX,
        offsetY,
        usableWidth: tvSafeWidth,
        usableHeight: tvSafeHeight,
        // CSS values for TV-safe positioning
        cssWidth: `${tvSafeWidth}px`,
        cssHeight: `${tvSafeHeight}px`,
        cssLeft: `${offsetX}px`,
        cssTop: `${offsetY}px`
      };
    }

    // For mobile/tablet/desktop, use full viewport
    return {
      width: windowWidth,
      height: windowHeight,
      offsetX: 0,
      offsetY: 0,
      usableWidth: windowWidth,
      usableHeight: windowHeight,
      cssWidth: '100vw',
      cssHeight: '100vh',
      cssLeft: '0',
      cssTop: '0'
    };
  }

  /**
   * Test browser capabilities for progressive enhancement
   */
  testCapabilities() {
    const capabilities = {
      // CSS support
      supportsViewportUnits: this.testCSSSupport('width', '100vw'),
      supportsFlexbox: this.testCSSSupport('display', 'flex'),
      supportsGrid: this.testCSSSupport('display', 'grid'),
      supportsObjectFit: this.testCSSSupport('object-fit', 'cover'),
      supportsBorderRadius: this.testCSSSupport('border-radius', '10px'),
      supportsTransform: this.testCSSSupport('transform', 'scale(1)'),
      
      // JavaScript APIs
      supportsLocalStorage: this.testLocalStorage(),
      supportsSessionStorage: this.testSessionStorage(),
      supportsMediaDevices: !!(navigator.mediaDevices),
      supportsGeolocation: !!(navigator.geolocation),
      
      // Media capabilities
      canPlayVideo: this.testVideoSupport(),
      canCreateIframe: this.testIframeSupport(),
      supportsAutoplay: this.deviceInfo.isTV || !this.deviceInfo.isMobile, // TVs and desktop usually allow autoplay
      
      // Performance indicators
      isHighPerformance: this.deviceInfo.isDesktop || (this.deviceInfo.isTV && !this.deviceInfo.isFireTV),
      isLowPerformance: this.deviceInfo.isMobile || this.deviceInfo.isFireTV,
    };

    return capabilities;
  }

  /**
   * Test CSS feature support
   */
  testCSSSupport(property, value) {
    try {
      return CSS.supports && CSS.supports(property, value);
    } catch (e) {
      return false;
    }
  }

  /**
   * Test localStorage availability
   */
  testLocalStorage() {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Test sessionStorage availability
   */
  testSessionStorage() {
    try {
      const testKey = '__test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Test video element support
   */
  testVideoSupport() {
    try {
      const video = document.createElement('video');
      return !!(video.canPlayType);
    } catch (e) {
      return false;
    }
  }

  /**
   * Test iframe creation support
   */
  testIframeSupport() {
    try {
      const iframe = document.createElement('iframe');
      iframe.src = 'about:blank';
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get optimal React Player configuration for current device
   */
  getReactPlayerConfig() {
    const baseConfig = {
      youtube: {
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: 0,
          controls: 0,
          showinfo: 0,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          widget_referrer: window.location.origin
        }
      }
    };

    // TV-specific optimizations - use highest available quality
    // YouTube will automatically adjust if bandwidth is insufficient
    if (this.deviceInfo.isTV) {
      baseConfig.youtube.playerVars = {
        ...baseConfig.youtube.playerVars,
        quality: 'highres',
        hd: 1,
        vq: 'highres'
      };
    }

    return baseConfig;
  }

  /**
   * Should we use ReactPlayer or direct iframe?
   */
  shouldUseReactPlayer() {
    // For TV browsers, direct iframe might be more reliable
    return !this.deviceInfo.isTV || this.capabilities.isHighPerformance;
  }

  /**
   * Get CSS styles for perfect viewport utilization
   */
  getViewportStyles() {
    return {
      mainContainer: {
        position: 'fixed',
        top: this.viewport.cssTop,
        left: this.viewport.cssLeft,
        width: this.viewport.cssWidth,
        height: this.viewport.cssHeight,
        overflow: 'hidden',
        // Ensure proper rendering on all devices
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        WebkitPerspective: '1000px',
        perspective: '1000px'
      },
      
      fullscreenContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }
    };
  }

  /**
   * Log device information for debugging
   */
  logDeviceInfo() {
    logger.info('Device detection complete', {
      device: this.deviceInfo,
      viewport: this.viewport,
      capabilities: this.capabilities
    }, 'DeviceManager');
  }
}

// Create singleton instance
const deviceManager = new DeviceManager();

// Log device info in development
if (process.env.NODE_ENV === 'development') {
  deviceManager.logDeviceInfo();
}

export default deviceManager;
